<template>
  <div class="step-panel" data-testid="payroll-step4-expense-conciliation">
    <h3>{{ t('Conciliação de Despesas') }}</h3>
    <p class="step-description">
      {{ t('Revise o status da conciliação de despesas e gerencie as despesas vinculadas') }}
    </p>

    <!-- Conciliation Status Card -->
    <div class="conciliation-card">

      <div class="card-body">
        <!-- Summary Grid -->
        <div class="summary-grid">
          <div class="summary-item">
            <div class="item-label">{{ t('Status') }}</div>
            <div class="item-value">
              <span class="status-indicator" :class="statusClass"></span>
              {{ t(statusText) }}
            </div>
          </div>

          <div class="summary-item">
            <div class="item-label">{{ t('Despesas Vinculadas') }}</div>
            <div class="item-value">{{ conciliationSummary.count || 0 }}</div>
          </div>

          <div class="summary-item highlight">
            <div class="item-label">{{ t('Valor Conciliado') }}</div>
            <div class="item-value">{{ n(parseFloat(String(conciliationSummary.amount ?? 0)) || 0, 'currency') }}</div>
          </div>
        </div>

        <!-- Action Button -->
        <div class="action-section">
          <Button
            :label="buttonLabel"
            :icon="buttonIcon"
            @click="openConciliationWizard"
            :severity="buttonSeverity"
            size="large"
            class="action-button"
            data-testid="payroll-step4-open-wizard-btn" />
        </div>

        <!-- Info Messages -->
        <div v-if="conciliationSummary.status === 'pending'" class="info-message pending">
          <i class="pi pi-info-circle"></i>
          <span>{{ t('Clique no botão acima para começar a vincular despesas a este período de folha.') }}</span>
        </div>

        <div v-else-if="conciliationSummary.status === 'in_progress'" class="info-message in-progress">
          <i class="pi pi-sync"></i>
          <span>{{ t('Conciliação em andamento. Você pode continuar adicionando despesas ou finalizar o processo.') }}</span>
        </div>

        <div v-else-if="conciliationSummary.status === 'completed'" class="info-message completed">
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Conciliação de despesas concluída. Você pode editar a conciliação clicando no botão acima.') }}</span>
        </div>
      </div>
    </div>

    <!-- ExpenseConciliationWizard -->
    <ExpenseConciliationWizard
      v-if="periodId"
      :visible="showConciliationWizard"
      :period-id="periodId"
      :franchise-id="franchiseId ?? undefined"
      :start-date="startDate ?? undefined"
      :end-date="endDate ?? undefined"
      :frequency="frequency"
      @update:visible="showConciliationWizard = $event"
      @completed="onConciliationCompleted"
      @closed="onWizardClosed" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import ExpenseConciliationWizard from './ExpenseConciliationWizard.vue';
import { useExpenseConciliation } from '../../../composables/useExpenseConciliation';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';

type Id = number | string;

interface ConciliationSummary {
  status: 'pending' | 'in_progress' | 'completed' | string;
  amount: number | string;
  count: number;
}

// XState wizard machine has a complex generated type. Using a focused
// structural interface here keeps strict TS happy without dragging the
// full XState typings into every step component.
interface WizardMachine {
  context: { value: {
    period?: {
      id?: Id;
      franchise_id?: Id;
      period_start_date?: string | null;
      period_end_date?: string | null;
      frequency?: string;
    };
    periodFormData?: {
      franchise_id?: Id | null;
      start_date?: string | null;
      end_date?: string | null;
      frequency?: string;
    };
  } };
  updateExpenses: (status: string, amount: number | string) => void;
}

// =============================================================================
// INJECT MACHINE - Single Source of Truth
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine')!;

// =============================================================================
// REACTIVE DATA FROM MACHINE
// =============================================================================

/**
 * Get period ID from machine context (converted to Number for prop type safety)
 */
const periodId = computed<number | null>(() => {
  const id = machine.context.value.period?.id;
  return id ? Number(id) : null;
});

/**
 * Get franchise ID from machine context
 */
const franchiseId = computed<number | null>(() => {
  return Number(machine.context.value.period?.franchise_id) ||
         Number(machine.context.value.periodFormData?.franchise_id) ||
         null;
});

/**
 * Get start date from machine context
 */
const startDate = computed<string | null>(() => {
  return machine.context.value.period?.period_start_date ||
         machine.context.value.periodFormData?.start_date ||
         null;
});

/**
 * Get end date from machine context
 */
const endDate = computed<string | null>(() => {
  return machine.context.value.period?.period_end_date ||
         machine.context.value.periodFormData?.end_date ||
         null;
});

/**
 * Get frequency from machine context
 */
type Frequency = 'weekly' | 'biweekly' | 'monthly';
const frequency = computed<Frequency>(() => {
  const raw = machine.context.value.period?.frequency ||
              machine.context.value.periodFormData?.frequency ||
              'monthly';
  return (raw === 'weekly' || raw === 'biweekly' || raw === 'monthly') ? raw : 'monthly';
});

// Composables
const { t, n } = useI18n();
const { getExpenseConciliationSummary } = useExpenseConciliation();

// State
const conciliationSummary = ref<ConciliationSummary>({
  status: 'pending',
  amount: 0,
  count: 0
});
const showConciliationWizard = ref<boolean>(false);

const statusClass = computed(() => {
  const status = conciliationSummary.value.status;
  return {
    'pending': status === 'pending',
    'in-progress': status === 'in_progress',
    'completed': status === 'completed'
  };
});

const statusText = computed<string>(() => {
  const status = conciliationSummary.value.status;
  switch (status) {
    case 'pending':
      return 'Pendente';
    case 'in_progress':
      return 'Em Andamento';
    case 'completed':
      return 'Concluído';
    default:
      return 'Desconhecido';
  }
});

const buttonLabel = computed<string>(() => {
  const status = conciliationSummary.value.status;
  switch (status) {
    case 'pending':
      return t('Iniciar Conciliação');
    case 'completed':
      return t('Editar Conciliação');
    default:
      return t('Continuar Conciliação');
  }
});

const buttonIcon = computed<string>(() => {
  const status = conciliationSummary.value.status;
  switch (status) {
    case 'pending':
      return 'pi pi-play';
    case 'completed':
      return 'pi pi-pencil';
    default:
      return 'pi pi-arrow-right';
  }
});

const buttonSeverity = computed<'primary' | 'secondary'>(() => {
  return conciliationSummary.value.status === 'completed' ? 'secondary' : 'primary';
});

// Methods
const loadConciliationSummary = async (): Promise<void> => {
  if (!periodId.value) {
    log('Step4ExpenseConciliationSummary', 'loadConciliationSummary', 'No period ID available yet');
    return;
  }

  log('Step4ExpenseConciliationSummary', 'loadConciliationSummary', 'Loading summary', {
    periodId: periodId.value
  });

  try {
    const summary = await getExpenseConciliationSummary(periodId.value) as ConciliationSummary;
    conciliationSummary.value = summary;

    // Update machine with conciliation data
    machine.updateExpenses(summary.status, summary.amount);

    log('Step4ExpenseConciliationSummary', 'loadConciliationSummary', 'Summary loaded', summary);
  } catch (error) {
    log('Step4ExpenseConciliationSummary', 'loadConciliationSummary', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar resumo da conciliação de despesas'));
  }
};

const openConciliationWizard = (): void => {
  if (!periodId.value) {
    eventBus.emit('globalMessage.warning', t('Por favor, complete os passos anteriores primeiro'));
    return;
  }

  log('Step4ExpenseConciliationSummary', 'openConciliationWizard', 'Opening wizard');
  showConciliationWizard.value = true;
};

const onConciliationCompleted = async (result: unknown): Promise<void> => {
  log('Step4ExpenseConciliationSummary', 'onConciliationCompleted', 'Conciliation completed', result);

  // Reload summary to show updated data
  await loadConciliationSummary();

  eventBus.emit('globalMessage.success', t('Conciliação de despesas concluída com sucesso'));
};

const onWizardClosed = async (): Promise<void> => {
  log('Step4ExpenseConciliationSummary', 'onWizardClosed', 'Wizard closed');

  // Reload summary to reflect any changes made in the wizard
  await loadConciliationSummary();
};


// Watch for period ID changes
watch(() => periodId.value, (newVal) => {
  if (newVal) {
    log('Step4ExpenseConciliationSummary', 'watch:periodId', 'Period ID available, loading summary', {
      periodId: newVal
    });
    loadConciliationSummary();
  }
}, { immediate: true });

// Watch for period date changes
watch(() => [startDate.value, endDate.value], () => {
  if (periodId.value) {
    loadConciliationSummary();
  }
});

// Lifecycle
onMounted(() => {
  log('Step4ExpenseConciliationSummary', 'mounted', 'Component mounted');

  if (periodId.value) {
    loadConciliationSummary();
  }

  // Listen for conciliation events
  eventBus.on('expense.conciliation.updated', loadConciliationSummary);
});
</script>

<style scoped>
.step-panel {
  width: 100%;
}

.step-panel h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

/* Conciliation Card */
.conciliation-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--surface-border);
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, var(--surface-50) 0%, var(--surface-100) 100%);
  border-bottom: 1px solid var(--surface-border);
}

.header-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  transition: all 0.3s ease;
}

.header-icon.pending {
  background: var(--yellow-100);
  color: var(--yellow-700);
}

.header-icon.in-progress {
  background: var(--blue-100);
  color: var(--blue-700);
}

.header-icon.completed {
  background: var(--green-100);
  color: var(--green-700);
}

.header-text {
  flex: 1;
}

.header-text h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.pending {
  background: var(--yellow-100);
  color: var(--yellow-900);
}

.status-badge.in-progress {
  background: var(--blue-100);
  color: var(--blue-900);
}

.status-badge.completed {
  background: var(--green-100);
  color: var(--green-900);
}

.card-body {
  padding: 2rem;
}

/* Summary Grid */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.summary-item {
  padding: 1.25rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  transition: all 0.3s ease;
}

.summary-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.summary-item.highlight {
  background: linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%);
  border-color: var(--primary-200);
}

.item-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color-secondary);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.item-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.summary-item.highlight .item-value {
  color: var(--primary-color);
  font-size: 1.75rem;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.status-indicator.pending {
  background: var(--yellow-500);
  animation: pulse-yellow 2s infinite;
}

.status-indicator.in-progress {
  background: var(--blue-500);
  animation: pulse-blue 2s infinite;
}

.status-indicator.completed {
  background: var(--green-500);
}

@keyframes pulse-yellow {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@keyframes pulse-blue {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Action Section */
.action-section {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
  margin-bottom: 1rem;
}

.action-button {
  min-width: 250px;
  font-size: 1.125rem;
  padding: 0.875rem 2rem;
}

/* Info Messages */
.info-message {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  line-height: 1.5;
}

.info-message i {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.info-message.pending {
  background: var(--yellow-50);
  border: 1px solid var(--yellow-200);
  color: var(--yellow-900);
}

.info-message.pending i {
  color: var(--yellow-600);
}

.info-message.in-progress {
  background: var(--blue-50);
  border: 1px solid var(--blue-200);
  color: var(--blue-900);
}

.info-message.in-progress i {
  color: var(--blue-600);
}

.info-message.completed {
  background: var(--green-50);
  border: 1px solid var(--green-200);
  color: var(--green-900);
}

.info-message.completed i {
  color: var(--green-600);
}

/* Responsive */
@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 1.25rem;
  }

  .header-icon {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  .card-body {
    padding: 1.5rem;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .item-value {
    font-size: 1.25rem;
  }

  .summary-item.highlight .item-value {
    font-size: 1.5rem;
  }

  .action-button {
    min-width: 100%;
    font-size: 1rem;
  }
}
</style>
